import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Camera, X, Plus, Image as ImageIcon, Gauge, Radio, MapPin, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

// Shared photo processing helper
const processPhotoFiles = async (files, maxPhotos, currentCount) => {
  if (currentCount + files.length > maxPhotos) {
    toast.error(`Maximum ${maxPhotos} photos`);
    return [];
  }
  return Promise.all(
    files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new window.Image();
            img.onload = () => {
              const canvas = document.createElement("canvas");
              const max = 1200;
              let w = img.width, h = img.height;
              if (w > h && w > max) { h = (h * max) / w; w = max; }
              else if (h > max) { w = (w * max) / h; h = max; }
              canvas.width = w;
              canvas.height = h;
              canvas.getContext("2d").drawImage(img, 0, 0, w, h);
              resolve(canvas.toDataURL("image/jpeg", 0.8));
            };
            img.onerror = reject;
            img.src = e.target.result;
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    )
  );
};

// Photo grid with optional value/comment field per photo
const PhotoGrid = ({ photos, label, valueLabel, onUpdate, testPrefix }) => {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [processing, setProcessing] = useState(false);

  const handleFiles = async (files) => {
    if (!files.length) return;
    setProcessing(true);
    try {
      const newPhotos = await processPhotoFiles(Array.from(files), 10, photos.length);
      if (newPhotos.length) {
        onUpdate([...photos, ...newPhotos.map((p) => ({ photo: p, [valueLabel === "Commentaire" ? "commentaire" : "valeur"]: "" }))]);
        toast.success(`${newPhotos.length} photo(s) ajoutée(s)`);
      }
    } catch { toast.error("Erreur photo"); }
    finally { setProcessing(false); }
  };

  const remove = (i) => onUpdate(photos.filter((_, idx) => idx !== i));

  const updateVal = (i, val) => {
    const key = valueLabel === "Commentaire" ? "commentaire" : "valeur";
    const updated = [...photos];
    updated[i] = { ...updated[i], [key]: val };
    onUpdate(updated);
  };

  return (
    <div>
      <Label className="text-sm mb-2 block">{label} ({photos.length}/10)</Label>
      <div className="flex gap-2 mb-3">
        <Button type="button" onClick={() => cameraRef.current?.click()} disabled={processing || photos.length >= 10} className="flex-1 h-10 bg-sky-600 hover:bg-sky-700 text-white text-sm" data-testid={`${testPrefix}-camera-btn`}>
          {processing ? <div className="spinner w-4 h-4 border-white border-t-transparent" /> : <><Camera className="w-4 h-4 mr-1" />Photo</>}
        </Button>
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={processing || photos.length >= 10} className="h-10 px-3 text-sm" data-testid={`${testPrefix}-gallery-btn`}>
          <Plus className="w-4 h-4 mr-1" />Galerie
        </Button>
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} className="hidden" />
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} className="hidden" />

      {photos.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {photos.map((item, i) => {
            const val = valueLabel === "Commentaire" ? item.commentaire : item.valeur;
            const showVal = valueLabel === "Commentaire" || (val && val !== "0");
            return (
              <div key={i} className="space-y-1.5">
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                  <img src={item.photo} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => remove(i)} className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <span className="absolute bottom-1.5 left-1.5 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">{i + 1}</span>
                </div>
                {showVal || valueLabel === "Commentaire" ? (
                  <input
                    type="text"
                    value={val || ""}
                    onChange={(e) => updateVal(i, e.target.value)}
                    placeholder={valueLabel + "..."}
                    className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-400"
                    data-testid={`${testPrefix}-photo-val-${i}`}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center">
          <ImageIcon className="w-6 h-6 mx-auto text-slate-300 mb-1" />
          <p className="text-slate-400 text-xs">Aucune photo</p>
        </div>
      )}
    </div>
  );
};

// ---- Default empty fuite factory ----
const createEmptyFuite = (num) => ({
  numero_fuite: num,
  description: "",
  numero_compteur: "",
  index_value: null,
  compteur_photos: [],
  methodes: {
    gaz_traceur: false,
    gaz_traceur_valeurs: "",
    gaz_traceur_photos: [],
    electroacoustique: false,
    electroacoustique_valeurs: "",
    electroacoustique_photos: [],
  },
  emplacement: "",
  emplacement_photos: [],
});

// ---- Normalize incoming data (backward compat) ----
const normalizeFuites = (raw) => {
  if (!raw) return [createEmptyFuite(1)];
  if (Array.isArray(raw)) return raw.length ? raw : [createEmptyFuite(1)];
  // Old single-object format → wrap in array with numero_fuite=1
  return [{ ...raw, numero_fuite: 1, description: raw.description || "" }];
};

// ---- Single fuite 3-block component ----
const FuiteBlocs = ({ fuite, fuiteIndex, totalFuites, onUpdate, onRemove }) => {
  const [collapsed, setCollapsed] = useState(false);
  const num = fuite.numero_fuite || fuiteIndex + 1;
  const prefix = `dc-f${num}`;

  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(fuite));
    const parts = path.split(".");
    let ref = next;
    for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
    ref[parts[parts.length - 1]] = value;
    onUpdate(next);
  };

  return (
    <div className="border-2 border-sky-200 rounded-2xl bg-sky-50/30 overflow-hidden" data-testid={`fuite-group-${num}`}>
      {/* Fuite header */}
      <div className="flex items-center justify-between bg-sky-100 px-4 py-3 border-b border-sky-200">
        <button type="button" onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 flex-1 text-left">
          {collapsed ? <ChevronDown className="w-4 h-4 text-sky-700" /> : <ChevronUp className="w-4 h-4 text-sky-700" />}
          <span className="font-bold text-sky-900 text-sm" data-testid={`fuite-title-${num}`}>
            Fuite N°{num}
          </span>
          {fuite.description && (
            <span className="text-sky-600 text-xs truncate max-w-[200px]">— {fuite.description}</span>
          )}
        </button>
        {totalFuites > 1 && fuiteIndex > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onRemove()}
            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
            data-testid={`fuite-remove-${num}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            <span className="text-xs">Supprimer</span>
          </Button>
        )}
      </div>

      {!collapsed && (
        <div className="p-4 space-y-5">
          {/* Description field */}
          <div>
            <Label className="text-sm font-medium text-slate-700">Description de la fuite</Label>
            <Input
              value={fuite.description || ""}
              onChange={(e) => update("description", e.target.value.slice(0, 60))}
              placeholder="ex : canalisation sous dalle, réseau encastré couloir..."
              maxLength={60}
              className="h-10 mt-1 text-sm"
              data-testid={`${prefix}-description`}
            />
            <p className="text-[10px] text-slate-400 mt-0.5 text-right">{(fuite.description || "").length}/60</p>
          </div>

          {/* BLOC 1 — COMPTEUR */}
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 bg-slate-50 px-4 py-3 border-b border-slate-200">
              <Gauge className="w-5 h-5 text-sky-600" />
              <span className="font-semibold text-slate-800 text-sm">1. Compteur</span>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">N° de compteur</Label>
                  <Input value={fuite.numero_compteur || ""} onChange={(e) => update("numero_compteur", e.target.value)} placeholder="Ex: 12345678" className="h-11 mt-1" data-testid={`${prefix}-numero-compteur`} />
                </div>
                <div>
                  <Label className="text-sm">Index relevé</Label>
                  <Input type="number" value={fuite.index_value ?? ""} onChange={(e) => update("index_value", e.target.value ? parseFloat(e.target.value) : null)} placeholder="Ex: 1234.56" className="h-11 mt-1" data-testid={`${prefix}-index-value`} />
                </div>
              </div>
              <PhotoGrid photos={fuite.compteur_photos || []} label="Photos du compteur" valueLabel="Valeur relevée" onUpdate={(v) => update("compteur_photos", v)} testPrefix={`${prefix}-compteur`} />
            </div>
          </div>

          {/* BLOC 2 — MÉTHODE DE RECHERCHE */}
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 bg-slate-50 px-4 py-3 border-b border-slate-200">
              <Radio className="w-5 h-5 text-sky-600" />
              <span className="font-semibold text-slate-800 text-sm">2. Méthode de recherche</span>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-slate-500">Sélectionnez les méthodes utilisées</p>
              {/* Gaz traceur */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all hover:bg-slate-50" onClick={() => update("methodes.gaz_traceur", !(fuite.methodes?.gaz_traceur))}>
                  <Checkbox checked={fuite.methodes?.gaz_traceur || false} onCheckedChange={(v) => update("methodes.gaz_traceur", v)} data-testid={`${prefix}-gaz-traceur-check`} />
                  <label className="text-sm font-medium cursor-pointer">Gaz traceur</label>
                </div>
                {fuite.methodes?.gaz_traceur && (
                  <div className="pl-4 border-l-2 border-sky-300 space-y-3">
                    <div>
                      <Label className="text-sm">Valeurs relevées</Label>
                      <Textarea value={fuite.methodes?.gaz_traceur_valeurs || ""} onChange={(e) => update("methodes.gaz_traceur_valeurs", e.target.value)} placeholder="Décrivez les valeurs relevées..." className="mt-1 min-h-[60px]" data-testid={`${prefix}-gaz-valeurs`} />
                    </div>
                    <PhotoGrid photos={fuite.methodes?.gaz_traceur_photos || []} label="Photos gaz traceur" valueLabel="Valeur relevée" onUpdate={(v) => update("methodes.gaz_traceur_photos", v)} testPrefix={`${prefix}-gaz`} />
                  </div>
                )}
              </div>
              {/* Électroacoustique */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all hover:bg-slate-50" onClick={() => update("methodes.electroacoustique", !(fuite.methodes?.electroacoustique))}>
                  <Checkbox checked={fuite.methodes?.electroacoustique || false} onCheckedChange={(v) => update("methodes.electroacoustique", v)} data-testid={`${prefix}-electro-check`} />
                  <label className="text-sm font-medium cursor-pointer">Électroacoustique</label>
                </div>
                {fuite.methodes?.electroacoustique && (
                  <div className="pl-4 border-l-2 border-sky-300 space-y-3">
                    <div>
                      <Label className="text-sm">Valeurs relevées</Label>
                      <Textarea value={fuite.methodes?.electroacoustique_valeurs || ""} onChange={(e) => update("methodes.electroacoustique_valeurs", e.target.value)} placeholder="Décrivez les valeurs relevées..." className="mt-1 min-h-[60px]" data-testid={`${prefix}-electro-valeurs`} />
                    </div>
                    <PhotoGrid photos={fuite.methodes?.electroacoustique_photos || []} label="Photos électroacoustique" valueLabel="Valeur relevée" onUpdate={(v) => update("methodes.electroacoustique_photos", v)} testPrefix={`${prefix}-electro`} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BLOC 3 — EMPLACEMENT */}
          <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 bg-slate-50 px-4 py-3 border-b border-slate-200">
              <MapPin className="w-5 h-5 text-sky-600" />
              <span className="font-semibold text-slate-800 text-sm">3. Emplacement</span>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <Label className="text-sm">Description de l'emplacement</Label>
                <Textarea value={fuite.emplacement || ""} onChange={(e) => update("emplacement", e.target.value)} placeholder="Décrivez l&#39;emplacement de la fuite..." className="mt-1 min-h-[80px]" data-testid={`${prefix}-emplacement`} />
              </div>
              <PhotoGrid photos={fuite.emplacement_photos || []} label="Photos de l'emplacement" valueLabel="Commentaire" onUpdate={(v) => update("emplacement_photos", v)} testPrefix={`${prefix}-emplacement`} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Main component ----
const DiagnosticCompteur = ({ data, onChange }) => {
  const fuites = normalizeFuites(data);

  const updateFuite = (index, updatedFuite) => {
    const next = fuites.map((f, i) => (i === index ? updatedFuite : JSON.parse(JSON.stringify(f))));
    onChange(next);
  };

  const addFuite = () => {
    const next = [...fuites.map(f => JSON.parse(JSON.stringify(f))), createEmptyFuite(fuites.length + 1)];
    onChange(next);
  };

  const removeFuite = (index) => {
    if (fuites.length <= 1) return;
    const next = fuites
      .filter((_, i) => i !== index)
      .map((f, i) => ({ ...f, numero_fuite: i + 1 }));
    onChange(next);
  };

  return (
    <div className="space-y-6" data-testid="diagnostic-compteur">
      {fuites.map((fuite, idx) => (
        <FuiteBlocs
          key={`fuite-${idx}-${fuite.numero_fuite}`}
          fuite={fuite}
          fuiteIndex={idx}
          totalFuites={fuites.length}
          onUpdate={(updated) => updateFuite(idx, updated)}
          onRemove={() => removeFuite(idx)}
        />
      ))}

      {/* Bouton ajouter une fuite */}
      <Button
        type="button"
        variant="outline"
        onClick={addFuite}
        className="w-full h-12 border-dashed border-2 border-sky-300 text-sky-700 hover:bg-sky-50 hover:border-sky-400 font-medium"
        data-testid="add-fuite-btn"
      >
        <Plus className="w-5 h-5 mr-2" />
        Ajouter une fuite
      </Button>
    </div>
  );
};

export default DiagnosticCompteur;
